import { useState } from 'react';
import {
  collection,
  addDoc,
  updateDoc,
  doc,
  getDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../lib/firebase';
import { SlotAsset } from '../components/generate/AssetSlotMapper';
import { Template } from './useTemplates';
import { toDateSafe } from '../lib/dateUtils';

/**
 * Serialisable representation of a single slot's state — same as SlotAsset
 * but without the un-serialisable `File` object.
 * Callers are responsible for uploading any local File objects and replacing
 * them with a durable storageUrl before calling saveDraft; this type only
 * carries fields that can safely be written to Firestore.
 */
export interface DraftSlotAsset {
  slotId: string;
  textValue: string | null;
  storageUrl?: string | null;
  renderUrl?: string | null;
  assetName?: string | null;
  assetSize?: number | null;
  cropAnchor: string;
  trimStartSeconds?: number | null;
  videoDurationSeconds?: number | null;
}

export interface DraftRecord {
  id: string;
  templateId: string;
  templateName: string;
  market: string;
  name: string;
  slotDraft: DraftSlotAsset[];
  createdAt: Date;
  updatedAt: Date;
}

/**
 * Recursively removes `undefined` values from an object/array so that the
 * result is safe to write to Firestore (which rejects `undefined` fields).
 * - Object keys whose value is `undefined` are omitted entirely.
 * - Array slots that are `undefined` are converted to `null` to preserve indices.
 */
function omitUndefinedDeep(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map((item) => (item === undefined ? null : omitUndefinedDeep(item)));
  }
  if (value !== null && typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      if (v !== undefined) {
        out[k] = omitUndefinedDeep(v);
      }
    }
    return out;
  }
  return value;
}

function serializeSlotAssets(slotAssets: SlotAsset[]): DraftSlotAsset[] {
  return slotAssets.map((sa) => {
    const entry: DraftSlotAsset = {
      slotId: sa.slotId,
      textValue: sa.textValue,
      cropAnchor: sa.cropAnchor,
      // File objects cannot be stored in Firestore.  Callers must upload any
      // local files before calling saveDraft so that storageUrl is populated.
      storageUrl: sa.storageUrl ?? null,
      renderUrl: sa.renderUrl ?? null,
      assetName: sa.assetName ?? null,
      assetSize: sa.assetSize ?? null,
      trimStartSeconds: sa.trimStartSeconds ?? null,
      videoDurationSeconds: sa.videoDurationSeconds ?? null,
    };
    return entry as DraftSlotAsset;
  });
}

export function useDraft() {
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  /**
   * Save or update a draft in the Firestore `jobs` collection.
   * If `existingDraftId` is provided the existing document is updated in-place;
   * otherwise a new document is created and its ID is returned.
   */
  const saveDraft = async (
    slotAssets: SlotAsset[],
    template: Template,
    market: string,
    existingDraftId: string | null,
  ): Promise<string> => {
    setSaving(true);
    try {
      const slotDraft = serializeSlotAssets(slotAssets);
      const name = `Draft — ${template.name}`;
      const now = serverTimestamp();

      if (existingDraftId) {
        await updateDoc(
          doc(db, 'jobs', existingDraftId),
          omitUndefinedDeep({
            slotDraft,
            name,
            market,
            updatedAt: now,
          }) as Record<string, unknown>,
        );
        return existingDraftId;
      }

      const docRef = await addDoc(
        collection(db, 'jobs'),
        omitUndefinedDeep({
          templateId: template.id,
          templateName: template.name,
          market,
          status: 'draft',
          name,
          jobSource: 'manual',
          slotDraft,
          props: {},
          progress: 0,
          createdAt: now,
          updatedAt: now,
          deliverableId: template.deliverable_id ?? null,
          format: template.format ?? null,
          outputFormat: template.output_format ?? null,
          templateVersion: template.template_version ?? null,
          remotionComponentId: template.remotionCompositionId ?? null,
        }) as Record<string, unknown>,
      );

      return docRef.id;
    } finally {
      setSaving(false);
    }
  };

  /** Soft-delete a draft document by marking it discarded. */
  const deleteDraft = async (draftId: string): Promise<void> => {
    setDeleting(true);
    try {
      await updateDoc(doc(db, 'jobs', draftId), {
        status: 'discarded',
        discardedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
    } finally {
      setDeleting(false);
    }
  };

  /** Mark a draft as consumed after a new render job has been created successfully. */
  const consumeDraft = async (draftId: string, linkedJobId: string): Promise<void> => {
    await updateDoc(doc(db, 'jobs', draftId), {
      status: 'consumed',
      consumedAt: serverTimestamp(),
      linkedJobId,
      updatedAt: serverTimestamp(),
    });
  };

  /** Fetch a single draft by ID. Returns null if not found or not a draft. */
  const getDraft = async (draftId: string): Promise<DraftRecord | null> => {
    const snap = await getDoc(doc(db, 'jobs', draftId));
    if (!snap.exists()) return null;
    const d = snap.data();
    if (d.status !== 'draft') return null;
    return {
      id: snap.id,
      templateId: d.templateId ?? '',
      templateName: d.templateName ?? '',
      market: d.market ?? 'global',
      name: d.name ?? '',
      slotDraft: Array.isArray(d.slotDraft) ? (d.slotDraft as DraftSlotAsset[]) : [],
      createdAt: toDateSafe(d.createdAt),
      updatedAt: toDateSafe(d.updatedAt),
    };
  };

  return { saveDraft, deleteDraft, consumeDraft, getDraft, saving, deleting };
}
